import {
  CLOUD_RUNTIME_UPDATE_ERROR_CODE,
  INTAKE_NO_CHECKPOINT_ERROR_CODE,
  type InterviewRecoverResult,
} from '@volley/crucible-ipc-types';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';

export const INTERVIEW_RECOVERY_RETRY_DELAYS_MS = [1_000, 3_000, 8_000] as const;

export interface RecoverInterviewWithBackoffOptions {
  recover: () => Promise<InterviewRecoverResult>;
  signal?: AbortSignal;
  sleep?: (delayMs: number, signal?: AbortSignal) => Promise<void>;
  shouldRetry?: (error: unknown) => boolean;
}

function sleepUntilRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(signal.reason);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      reject(signal?.reason);
    };

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

/**
 * Retries only rejected recovery calls. A successful authoritative result is
 * final, including `not-needed`; retrying that result could start a duplicate
 * turn after another client has already recovered the interview.
 */
export async function recoverInterviewWithBackoff({
  recover,
  signal,
  sleep = sleepUntilRetry,
  shouldRetry = () => true,
}: RecoverInterviewWithBackoffOptions): Promise<InterviewRecoverResult> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= INTERVIEW_RECOVERY_RETRY_DELAYS_MS.length; attempt += 1) {
    if (signal?.aborted) throw signal.reason;

    try {
      return await recover();
    } catch (error) {
      lastError = error;
      if (!shouldRetry(error)) break;
      const delayMs = INTERVIEW_RECOVERY_RETRY_DELAYS_MS[attempt];
      if (delayMs === undefined) break;
      await sleep(delayMs, signal);
    }
  }

  throw lastError;
}

export function isCloudRuntimeUpdateRequired(error: unknown): boolean {
  return (
    (typeof error === 'object' &&
      error !== null &&
      (error as { code?: unknown }).code === CLOUD_RUNTIME_UPDATE_ERROR_CODE) ||
    unflattenIpcError(error).code === CLOUD_RUNTIME_UPDATE_ERROR_CODE
  );
}

/**
 * crucible#3697 — true when `error` is the structured "no adaptive-intake
 * checkpoint to recover" signal (`INTAKE_NO_CHECKPOINT_ERROR_CODE`): an
 * EXPECTED domain state (a game created via the `game:create`
 * `skipInteractiveIntake` bypass never writes a checkpoint), not a fault.
 * Checked via the structured `code` field decoded through `unflattenIpcError`
 * — never by matching on the error's freeform message text.
 */
export function isNoRecoverableIntakeCheckpoint(error: unknown): boolean {
  return (
    (typeof error === 'object' &&
      error !== null &&
      (error as { code?: unknown }).code === INTAKE_NO_CHECKPOINT_ERROR_CODE) ||
    unflattenIpcError(error).code === INTAKE_NO_CHECKPOINT_ERROR_CODE
  );
}

export function formatInterviewRecoveryError(error: unknown, fallbackPrefix: string): string {
  if (isCloudRuntimeUpdateRequired(error) && error instanceof Error) {
    const recovery = (error as Error & { recovery?: unknown }).recovery;
    return typeof recovery === 'string' ? `${error.message}. ${recovery}` : error.message;
  }
  const decoded = unflattenIpcError(error);
  if (decoded.code === CLOUD_RUNTIME_UPDATE_ERROR_CODE) {
    return decoded.recovery ? `${decoded.message}. ${decoded.recovery}` : decoded.message;
  }
  return `${fallbackPrefix}: ${decoded.message}`;
}
